<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { initScrollSpy } from "@utils/initScrollSpy.js";
	import translations from "./TermsOfService.translations.json" with { type: "json" };

	const translate = getTranslate(translations);

	const sections = [
		{ id: "intro", key: "intro" },
		{ id: "user-account", key: "userAccount" },
		{ id: "purchase-contract", key: "purchase-contract-enter" },
		{ id: "price-payment", key: "price-payment" },
		{ id: "withdrawal", key: "withdrawal" },
		{ id: "transport-delivery", key: "transport-delivery" },
		{ id: "defective-performance", key: "rights-from-defective-performance" },
		{ id: "subscription", key: "subscription-heading" },
		{ id: "courses", key: "courses-heading" },
		{ id: "others", key: "others" },
		{ id: "communication", key: "communication" },
	] as const;

	let activeId: string = $state(sections[0].id);
</script>

<Layout>
	<div
		class="tos-layout container-10"
		use:initScrollSpy={{
			sections,
			setActiveId: (id): void => {
				activeId = id;
			},
		}}
	>
		<nav class="tos-nav">
			<div class="sticky top-28">
				<p class="text-xs font-semibold uppercase tracking-wider text-fcrdarkgray/40 mb-4 pl-4">Obsah</p>
				<ul class="text-sm leading-relaxed">
					{#each sections as section (section.id)}
						<li>
							<a
								href="#{section.id}"
								class="block py-1.5 transition-colors duration-200 border-l-2 pl-4 {activeId === section.id
									? 'border-fcrdarkblue text-fcrdarkblue font-semibold'
									: 'border-fcrdarkgray/10 text-fcrdarkgray/40 hover:text-fcrdarkgray/70 hover:border-fcrdarkgray/30'}"
							>
								<Formatted text={translate(section.key)} />
							</a>
						</li>
					{/each}
				</ul>
			</div>
		</nav>

		<section class="tos-content font-medium">
			<Heading level={1} text={translate("tos")} class="heading-1 mb-6" />
			<p class="mb-2 text-base text-fcrdarkgray/70">
				<Formatted text={translate("tosAuthor")} />
			</p>
			<p class="mb-10 text-sm text-fcrdarkgray/50">
				<Formatted text={translate("effective-date")} />
			</p>

			<Heading level={2} text={translate("intro")} id="intro" class="heading-2 mt-16 mb-6" />
			<ol>
				<li><Formatted text={translate("tos-are")} /></li>
				<li><Formatted text={translate("seller-is")} /></li>
				<li><Formatted text={translate("buyer-is")} /></li>
				<li><Formatted text={translate("consumer-and-entrepreneur-are")} /></li>
				<li><Formatted text={translate("entrepreneur-civil-code-applies")} /></li>
				<li>
					<Formatted text={translate("contacts")} />
					<ol>
						<li><Formatted text={translate("email")} /></li>
						<li><Formatted text={translate("phone")} /></li>
					</ol>
				</li>
				<li><Formatted text={translate("shop")} /></li>
				<li><Formatted text={translate("purchase-contract-takes-precedence")} /></li>
				<li><Formatted text={translate("tos-part-of-purchase-contract")} /></li>
				<li><Formatted text={translate("seller-may-change-tos")} /></li>
				<li><Formatted text={translate("right-to-trademark-not-purchased")} /></li>
				<li><Formatted text={translate("buyer-agreed-tos-when-ordered")} /></li>
			</ol>

			<Heading level={2} text={translate("userAccount")} id="user-account" class="heading-2 mt-16 mb-6" />
			<ol>
				<li><Formatted text={translate("sign-up-unlocks-client-zone")} /></li>
				<li><Formatted text={translate("valid-and-truthful-information-required")} /></li>
				<li><Formatted text={translate("buyer-keeps-credentials-secret")} /></li>
				<li><Formatted text={translate("buyer-must-not-involve-3rd-parties")} /></li>
				<li><Formatted text={translate("seller-may-terminate")} /></li>
				<li><Formatted text={translate("seller-may-maintain")} /></li>
			</ol>

			<Heading
				level={2}
				text={translate("purchase-contract-enter")}
				id="purchase-contract"
				class="heading-2 mt-16 mb-6"
			/>
			<ol>
				<li><Formatted text={translate("informative-nature")} /></li>
				<li><Formatted text={translate("prices-include-taxes-may-change")} /></li>
				<li><Formatted text={translate("delivery-info-included")} /></li>
				<li>
					<Formatted text={translate("to-order-fill-in")} />
					<ol>
						<li><Formatted text={translate("billing-delivery-data")} /></li>
						<li><Formatted text={translate("ordered-products")} /></li>
						<li><Formatted text={translate("payment-method")} /></li>
						<li><Formatted text={translate("delivery-method")} /></li>
					</ol>
				</li>
				<li><Formatted text={translate("required-fields")} /></li>
				<li><Formatted text={translate("buyer-may-recheck-then-order-is-sent")} /></li>
				<li><Formatted text={translate("seller-may-check")} /></li>
				<li><Formatted text={translate("tos-apply-when-order-accepted")} /></li>
				<li>
					<Formatted text={translate("contract-may-be-changed-or-cancelled")} />
					<ol>
						<li><Formatted text={translate("cancel-by-email")} /></li>
						<li><Formatted text={translate("cancel-by-phone")} /></li>
					</ol>
				</li>
				<li><Formatted text={translate("buyer-accepts-distance-communication")} /></li>
				<li><Formatted text={translate("purchase-contract-may-not-be-entered-by-seller")} /></li>
				<li><Formatted text={translate("purchase-contract-may-be-declared-null-and-void")} /></li>
			</ol>

			<Heading level={2} text={translate("price-payment")} id="price-payment" class="heading-2 mt-16 mb-6" />
			<ol>
				<li>
					<Formatted text={translate("payment-methods")} />
					<ol>
						<li><Formatted text={translate("payment-card")} /></li>
						<li><Formatted text={translate("apple-pay")} /></li>
						<li><Formatted text={translate("google-pay")} /></li>
						<li><Formatted text={translate("qr-code")} /></li>
						<li><Formatted text={translate("in-cash-on-delivery")} /></li>
						<li><Formatted text={translate("in-cash-at-place")} /></li>
						<li><Formatted text={translate("invoice")} /></li>
					</ol>
				</li>
				<li><Formatted text={translate("prices-at-website-are-round-up")} /></li>
				<li><Formatted text={translate("delivery-paid-as-well")} /></li>
				<li><Formatted text={translate("pay-before")} /></li>
				<li><Formatted text={translate("when-cash")} /></li>
				<li><Formatted text={translate("when-non-cash")} /></li>
				<li><Formatted text={translate("pay-before-forced")} /></li>
				<li><Formatted text={translate("discounts-may-not-be-combined")} /></li>
				<li><Formatted text={translate("invoice-will-be-created-and-sent")} /></li>
				<li><Formatted text={translate("billing-data-may-not-be-changed")} /></li>
			</ol>

			<Heading level={2} text={translate("withdrawal")} id="withdrawal" class="heading-2 mt-16 mb-6" />
			<ol>
				<li><Formatted text={translate("withdrawal-may-not-be-possible-due-to-legal-reasons")} /></li>
				<li><Formatted text={translate("how-to-withdraw")} /></li>
				<li><Formatted text={translate("goods-return-buyers-pays")} /></li>
				<li><Formatted text={translate("money-return")} /></li>
				<li><Formatted text={translate("buyer-may-charge-damage")} /></li>
				<li><Formatted text={translate("seller-may-withdraw")} /></li>
				<li><Formatted text={translate("gifts-return-too")} /></li>
				<li>
					<Formatted text={translate("seller-may-cancel-if")} />
					<ol>
						<li><Formatted text={translate("goods-no-longer-manufactured")} /></li>
						<li><Formatted text={translate("supplier-highered-up-too-much")} /></li>
					</ol>
					<Formatted text={translate("in-that-case-seller-will-contact")} />
				</li>
				<li><Formatted text={translate("delivery-will-be-established-for-special-orders")} /></li>
			</ol>

			<Heading level={2} text={translate("transport-delivery")} id="transport-delivery" class="heading-2 mt-16 mb-6" />
			<ol>
				<li>
					<Formatted text={translate("delivery-methods")} />
					<ol>
						<li><Formatted text={translate("ppl-address")} /></li>
						<li><Formatted text={translate("ppl-parcel-shop")} /></li>
						<li><Formatted text={translate("ppl-parcel-box")} /></li>
						<li><Formatted text={translate("pickup-ostrava")} /></li>
						<li><Formatted text={translate("pickup-prague")} /></li>
						<li><Formatted text={translate("electronically")} /></li>
					</ol>
				</li>
				<li><Formatted text={translate("carrier-selection")} /></li>
				<li><Formatted text={translate("delivery-pricing")} /></li>
				<li><Formatted text={translate("dispatch")} /></li>
				<li><Formatted text={translate("tracking")} /></li>
				<li><Formatted text={translate("no-package-charge")} /></li>
				<li><Formatted text={translate("special-request-bears-a-risk")} /></li>
				<li><Formatted text={translate("buyer-must-pickup")} /></li>
				<li><Formatted text={translate("special-cases-paid-by-buyer")} /></li>
				<li><Formatted text={translate("buyer-must-check")} /></li>
				<li><Formatted text={translate("package-damaged")} /></li>
				<li><Formatted text={translate("id-may-be-required")} /></li>
				<li><Formatted text={translate("if-goods-not-collected")} /></li>
			</ol>

			<Heading
				level={2}
				text={translate("rights-from-defective-performance")}
				id="defective-performance"
				class="heading-2 mt-16 mb-6"
			/>
			<ol>
				<li><Formatted text={translate("laws-apply")} /></li>
				<li>
					<Formatted text={translate("seller-guarantees")} />
					<ol>
						<li><Formatted text={translate("goods-have-the-characteristics")} /></li>
						<li><Formatted text={translate("goods-are-fit-for-purpose")} /></li>
						<li><Formatted text={translate("goods-good")} /></li>
						<li><Formatted text={translate("correct-amount")} /></li>
						<li><Formatted text={translate("goods-legal")} /></li>
					</ol>
				</li>
				<li><Formatted text={translate("previously-known-deffects-lower-the-price")} /></li>
				<li><Formatted text={translate("half-a-year-two-year-guarantee")} /></li>
				<li><Formatted text={translate("where-to-apply")} /></li>
				<li><Formatted text={translate("self-caused-defect-does-not-count")} /></li>
				<li><Formatted text={translate("goods-exchanged")} /></li>
				<li><Formatted text={translate("deffects-must-be-announced-quickly")} /></li>
				<li><Formatted text={translate("ínternal-procedure-applies")} /></li>
				<li><Formatted text={translate("coffee-specific-complaints")} /></li>
			</ol>

			<Heading level={2} text={translate("subscription-heading")} id="subscription" class="heading-2 mt-16 mb-6" />
			<ol>
				<li><Formatted text={translate("subscription-what-is")} /></li>
				<li><Formatted text={translate("subscription-recurring")} /></li>
				<li><Formatted text={translate("subscription-gift")} /></li>
				<li><Formatted text={translate("subscription-cancellation")} /></li>
				<li><Formatted text={translate("subscription-delivery")} /></li>
				<li><Formatted text={translate("subscription-withdrawal")} /></li>
			</ol>

			<Heading level={2} text={translate("courses-heading")} id="courses" class="heading-2 mt-16 mb-6" />
			<ol>
				<li><Formatted text={translate("course-what-is")} /></li>
				<li><Formatted text={translate("course-confirmation")} /></li>
				<li><Formatted text={translate("course-cancellation-by-buyer")} /></li>
				<li><Formatted text={translate("course-cancellation-by-seller")} /></li>
				<li><Formatted text={translate("course-withdrawal")} /></li>
			</ol>

			<Heading level={2} text={translate("others")} id="others" class="heading-2 mt-16 mb-6" />
			<ol>
				<li><Formatted text={translate("ownership-emerges")} /></li>
				<li><Formatted text={translate("no-code-of-conduct")} /></li>
				<li><Formatted text={translate("czech-trade-authority")} /></li>
				<li><Formatted text={translate("data-protection-office")} /></li>
				<li><Formatted text={translate("buyer-risks-changes")} /></li>
			</ol>

			<Heading level={2} text={translate("communication")} id="communication" class="heading-2 mt-16 mb-6" />
			<ol>
				<li><Formatted text={translate("communication-electronic")} /></li>
			</ol>
		</section>
	</div>
</Layout>

<style lang="postcss">
	.tos-layout {
		@apply flex gap-12 py-10 lg:gap-20 lg:py-16;
	}

	.tos-nav {
		@apply hidden shrink-0 lg:block;
		width: 240px;
	}

	.tos-content {
		@apply min-w-0 text-base leading-relaxed;
		max-width: min(55ch, calc(100vw - var(--container-gap) * 2));
	}

	ol {
		list-style-type: decimal;
		padding-left: 2.5ch;

		> li > ol {
			list-style-type: lower-alpha;
		}

		> li {
			margin: 0.75rem 0;
		}
	}

	.tos-content {
		counter-reset: section;

		> :global(h2) {
			counter-increment: section;
			scroll-margin-top: 7rem;

			&::before {
				content: counter(section, upper-roman) ". ";
			}
		}
	}
</style>
